import type { Template } from '@cognite/apm-client/src/templates/types';
import { Infobox } from '@cognite/cogs.js-v10';
import { useTranslation } from '@infield/features/i18n';
import { LOCIZE_NAMESPACES } from '@infield/features/i18n';
import { SearchFilter } from '@infield/features/search';
import { ComponentScrollableContent } from '@infield/features/ui';
import { ComponentErrorBoundary } from '@infield/features/ui/component-error-boundary';
import { FooterMobile } from '@infield/features/ui/footer/footer-mobile/footer-mobile';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';

import { TemplateListUi } from '../template-list-ui';

import { TemplateListMobileContext } from './template-list-mobile.context';

export type TemplateListUserViewProps = {
  templates: Template[];
  isLoading: boolean;
};

/**
 * TemplateListMobile is a mobile-friendly component for displaying and interacting with
 * a list of templates. It allows the user to:
 * - Search and filter templates.
 * - Select a template to enable checklist creation.
 * - Create a checklist based on a selected template with a planned duration of 6 hours.
 * - Handle errors during checklist creation with retry and cancellation options.
 *
 * Note:
 * - Uses context hooks from TemplateListMobileContext for navigation, selection, search, and mutation.
 * - Displays an error infobox and footer actions conditionally based on state.
 *
 * Props:
 * - `templates`: List of available templates to display.
 * - `isLoading`: Indicates if the template list is still loading.
 */

export const TemplateListMobile: FC<TemplateListUserViewProps> = ({
  templates,
  isLoading,
}) => {
  const { t } = useTranslation(LOCIZE_NAMESPACES.template);
  const [showFooter, setShowFooter] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);

  const {
    useNavigate,
    useTemplateCreateChecklist,
    useSearchQuery,
    useTemplateSelection,
    useCurrentUserContext,
  } = useContext(TemplateListMobileContext);

  const navigate = useNavigate();
  const { mutateAsync: createChecklist, isLoading: isCreateChecklistLoading } =
    useTemplateCreateChecklist();

  const { debouncedQuery, fetchMode } = useSearchQuery();

  const { selectedTemplate, setSelectedTemplate } = useTemplateSelection();
  const { user: currentUser } = useCurrentUserContext();

  useEffect(() => {
    setShowFooter(
      templates.some(
        (template) => template.externalId === selectedTemplate?.externalId
      )
    );
    setShowError(false);
  }, [templates, selectedTemplate]);

  const handleSelectTemplate = (template: Template) => {
    setSelectedTemplate(template);
  };

  const handleCreateChecklist = () => {
    if (!selectedTemplate) {
      return;
    }

    createChecklist(
      {
        templateExternalId: selectedTemplate?.externalId || '',
        plannedStartTime: dayjs().utc().toISOString(),
        plannedEndTime: dayjs().add(6, 'hour').utc().toISOString(),
        assignedTo: [currentUser.externalId],
      },
      {
        onSuccess: ({ createdChecklist }) => {
          setSelectedTemplate(undefined);
          navigate({
            pathname: `/templates/createdChecklist/${createdChecklist.externalId}/`,
          });
        },
        onError: () => {
          setShowError(true);
        },
      }
    );
  };

  const handleCancelClick = () => {
    setSelectedTemplate(undefined);
  };

  return (
    <ComponentErrorBoundary sentryTag="TemplateListMobile">
      <ComponentScrollableContent>
        <SearchFilter
          searchPlaceholder={t(
            'TEMPLATE_LIST_SEARCH_PLACEHOLDER',
            'Search by name'
          )}
        />
        {showError && (
          <Infobox
            status="warning"
            title={t(
              'TEMPLATE_MOBILE_CREATE_ERROR_TITLE',
              'Checklist not created'
            )}
          >
            {t(
              'TEMPLATE_MOBILE_CREATE_ERROR_DESCRIPTION',
              'Try again or contact support if issue continues'
            )}
          </Infobox>
        )}
        <TemplateListUi
          isLoading={isLoading}
          templates={templates}
          fetchMode={fetchMode}
          debouncedQuery={debouncedQuery}
          selectedTemplate={selectedTemplate}
          handleSelectTemplate={handleSelectTemplate}
        />
        {showFooter && (
          <FooterMobile
            onLeftButtonClick={handleCancelClick}
            onRightButtonClick={handleCreateChecklist}
            leftButtonText={t('TEMPLATE_MOBILE_CANCEL', 'Cancel')}
            rightButtonText={
              showError
                ? t('TEMPLATE_MOBILE_RETRY', 'Retry')
                : t('TEMPLATE_MOBILE_CREATE_CHECKLIST', 'Create')
            }
            loading={isCreateChecklistLoading}
            rightButtonPrimary={true}
          />
        )}
      </ComponentScrollableContent>
    </ComponentErrorBoundary>
  );
};
